import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { analyticsPageForPath, initializeAnalytics, trackEvent } from './analytics';

import Login from "./components/Auth/Login";
import Register from "./components/Auth/Register";
import ForgotPassword from "./components/Auth/ForgotPassword";

import { AppLayout } from "./apps/Modquiz/index";

import { modquizRoutes } from "./apps/Modquiz"
import IssueList from "./apps/Training/IssueList"
import IssueWorkbench from "./apps/Training/IssueWorkbench"
import { RootStoreProvider } from "./hooks/useRootStore"
import { Outlet, useLocation } from "react-router-dom"


import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import AppHeader from './components/AppHeader';
import RadioApplet from './components/RadioApplet';
import { AudioPlayerProvider } from './components/AudioPlayer/AudioPlayerContext';
import { initializeTheme } from './theme';

const Admin = React.lazy(() => import('./apps/Metrics'));

const DEFAULT_ROUTE = "news";

initializeTheme();

initializeAnalytics();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

function RootLayout() {
  const location = useLocation();

  useEffect(() => {
    const page = analyticsPageForPath(location.pathname);
    if (page) trackEvent("Page Viewed", { page, path: location.pathname });
  }, [location.pathname]);

  return (
    <AudioPlayerProvider>
      <AppHeader />
      <Outlet />
      <RadioApplet />
    </AudioPlayerProvider>
  );
}

const router = [
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route={DEFAULT_ROUTE} />
          </div>
        ),
      },
      {
        path: "/news",
        element: <App route="news" />,
      },
      {
        path: "/haiku",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="haiku" />
          </div>
        ),
      },
      {
        path: "/earthytrees",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="earthytrees" />
          </div>
        ),
      },
      {
        path: "/macroeconomics",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="macroeconomics" />
          </div>
        ),
      },
      {
        path: "/historic_news",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news" />
          </div>
        ),
      },
      {
        path: "/historic_news/submit",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news_submit" />
          </div>
        ),
      },
      {
        path: "/historic_news/articles",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news_articles" />
          </div>
        ),
      },
      {
        path: "/historic_news/articles/:id",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news_article_show" />
          </div>
        ),
      },
      {
        path: "/historic_news/story/:id",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news_article_show" />
          </div>
        ),
      },
      {
        path: "/historic_news/story/:id/debug",
        element: <App route="historic_news_article_debug" />,
      },
      {
        path: "/admin/historic-news/articles/:id/fix",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news_article_debug" />
          </div>
        ),
      },
      {
        path: "/historic_news/corrections",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="historic_news_corrections" />
          </div>
        ),
      },
      {
        path: "/timeline",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="timeline" />
          </div>
        ),
      },
      {
        path: "/earthymaw",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="earthymaw" />
          </div>
        ),
      },
      {
        path: "/vibes",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="vibes" />
          </div>
        ),
      },
      {
        path: "/radio",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="radio" />
          </div>
        ),
      },
      {
        path: "/classical",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="classical" />
          </div>
        ),
      },
      {
        path: "/modquiz",
        element: (
          <AppLayout>
            <Outlet />
          </AppLayout>
        ),
        children: modquizRoutes,
      },
      {
        path: "/vibes/:entryId/media/:mediaIndex",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="vibes" />
          </div>
        ),
      },
      {
        path: "/vibes/:entryId/media/:mediaIndex/feed",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="vibes" />
          </div>
        ),
      },
      {
        path: "/feeds/:feed",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="entries" />
          </div>
        ),
      },
      {
        path: "/tags/:category",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="entries" />
          </div>
        ),
      },
      {
        path: "/entries/:integration/:integration_object_id",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="entry" />
          </div>
        ),
      },
      {
        path: "/creators",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="creators" />
          </div>
        ),
      },
      {
        path: "/creators/:creator",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="creator" />
          </div>
        ),
      },
      {
        path: "/integrations",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="integrations" />
          </div>
        ),
      },
      {
        path: "/integrations/:integration",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="integration" />
          </div>
        ),
      },
      {
        path: "/tags",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="tags" />
          </div>
        ),
      },
      {
        path: "/audit",
        element: (
          <div className="max-md:overflow-x-hidden">
            <App route="audit" />
          </div>
        ),
      },
      {
        path: "/admin",
        element: (
          <React.Suspense fallback={<div className="min-h-screen bg-stone-100 p-6 font-mono text-stone-600 dark:bg-zinc-900 dark:text-stone-400">Loading admin…</div>}>
            <Admin />
          </React.Suspense>
        ),
      },
      ...(process.env.NODE_ENV === "development"
        ? [
            {
              path: "/training",
              element: (
                <div className="max-md:overflow-x-hidden">
                  <App route="training" />
                </div>
              ),
            },
            {
              path: "/training/issues",
              element: (
                <div className="max-md:overflow-x-hidden">
                  <IssueList />
                </div>
              ),
            },
            {
              path: "/training/issues/:magazine/:date",
              element: (
                <div className="max-md:overflow-x-hidden">
                  <IssueWorkbench />
                </div>
              ),
            },
          ]
        : []),
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "register",
        element: <Register />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
    ],
  },
];

const browserRouter = createBrowserRouter(router);

root.render(
  <React.StrictMode>
    <RootStoreProvider>
      <RouterProvider router={browserRouter} />
    </RootStoreProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

export {};
